import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import clinicAPI from "../../../services/clinicApi";
import { Button, useToast } from "../../ui";
import { dispatchClientRefresh } from "../../../hooks/useClientRefresh";
import { useClientAthlete } from "../../../context/ClientAthleteContext";
import s from "./clientApp.module.css";

export default function ClientPayMembershipButton({
  membership,
  patientId,
  onCancelled,
}) {
  const toast = useToast();
  const { athleteQueryParam } = useClientAthlete();
  const [searchParams, setSearchParams] = useSearchParams();
  const [available, setAvailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const pending = membership?.status === "pending";
  const membershipId = membership?.id;

  useEffect(() => {
    if (!pending || !membershipId) {
      setAvailable(false);
      setLoading(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    clinicAPI
      .getOnlinePaymentAvailable({ membership_id: membershipId })
      .then((res) => {
        if (!cancelled) setAvailable(Boolean(res.available));
      })
      .catch(() => {
        if (!cancelled) setAvailable(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pending, membershipId]);

  const trySync = async ({ clearParams } = {}) => {
    const res = await clinicAPI.syncOnlineMembershipPayment({
      membership_id: membershipId,
      patient_id: patientId || undefined,
    });
    if (res.status === "confirmed") {
      toast.success("Оплата прошла — абонемент обновлён");
      if (clearParams) {
        const next = new URLSearchParams(searchParams);
        next.delete("payment");
        next.delete("membership");
        setSearchParams(next, { replace: true });
      }
      window.setTimeout(() => window.location.reload(), 500);
      return true;
    }
    return false;
  };

  // После return_url и при открытии экрана с pending — дожимаем оплату
  useEffect(() => {
    if (!pending || !membershipId) return undefined;

    const fromReturn = searchParams.get("payment") === "success";
    let cancelled = false;
    setSyncing(true);

    trySync({ clearParams: fromReturn })
      .then((ok) => {
        if (cancelled || ok || !fromReturn) return;
        toast.info(
          "Если оплата уже прошла — нажмите «Проверить оплату / оплатить».",
        );
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setSyncing(false);
      });

    return () => {
      cancelled = true;
    };
    // только при появлении pending / успешного return
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, membershipId]);

  if (!pending || loading) return null;

  const pay = async () => {
    setPaying(true);
    try {
      const synced = await trySync({ clearParams: true });
      if (synced) return;

      const res = await clinicAPI.initOnlineMembershipPayment({
        membership_id: membershipId,
        patient_id: patientId || undefined,
      });
      if (res.status === "confirmed") {
        toast.success("Оплата найдена — абонемент обновлён");
        window.setTimeout(() => window.location.reload(), 500);
        return;
      }
      if (res.payment_url) {
        window.location.href = res.payment_url;
        return;
      }
      toast.error("Не получена ссылка на оплату");
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось создать платёж");
    } finally {
      setPaying(false);
    }
  };

  const cancelOrder = async () => {
    if (
      !window.confirm(
        "Отменить заказ? Неоплаченный абонемент будет снят — можно будет выбрать тариф заново.",
      )
    ) {
      return;
    }
    setCancelling(true);
    try {
      const res = await clinicAPI.cancelMyMembership(
        membershipId,
        athleteQueryParam || {},
      );
      if (res.status === "confirmed") {
        toast.success(
          res.message || "Оплата уже прошла — абонемент активирован",
        );
        window.setTimeout(() => window.location.reload(), 400);
        return;
      }
      toast.success("Заказ отменён");
      onCancelled?.();
      dispatchClientRefresh();
      window.setTimeout(() => window.location.reload(), 300);
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось отменить");
    } finally {
      setCancelling(false);
    }
  };

  const busy = paying || syncing || cancelling;

  return (
    <div className={s.profClubBtnRow}>
      {available ? (
        <Button
          type="button"
          onClick={pay}
          disabled={busy}
          className={s.profClubBtn}
        >
          {syncing
            ? "Проверяем оплату…"
            : paying
              ? "Проверка…"
              : "Проверить оплату / оплатить"}
        </Button>
      ) : null}
      <Button
        type="button"
        variant="secondary"
        onClick={cancelOrder}
        disabled={busy}
        className={s.profClubBtn}
      >
        {cancelling ? "Отмена…" : "Отменить заказ"}
      </Button>
    </div>
  );
}
